import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Mail, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '@/contexts/language';
import { logger } from '@/utils/logger';
import { isBetaEnvironment } from '@/lib/betaConfig';
import { prepareBetaSignup, getBetaMetadata, trackBetaSignup } from '@/lib/betaHelpers';
import { EmailVerificationScreen } from './EmailVerificationScreen';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'login' | 'signup';
  onModeChange: (mode: 'login' | 'signup') => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  mode,
  onModeChange
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');

  const [loginData, setLoginData] = useState({
    email: '',
    password: '' });

  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    confirmPassword: '' });

  // Reset form when mode changes
  useEffect(() => {
    setLoginData({ email: '', password: '' });
    setSignupData({ email: '', password: '', confirmPassword: '' });
    setShowPassword(false);
    setShowEmailVerification(false);
    setPendingVerificationEmail('');
  }, [mode]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginData.email,
        password: loginData.password,
      });

      if (error) throw error;

      if (data.user) {
        // Check if user belongs to this platform (prevent cross-platform login)
        const userSource = data.user.user_metadata?.source;
        if (userSource === 'masubventionpro') {
          // User registered on MaSubventionPro - sign them out and show error
          await supabase.auth.signOut();
          throw new Error('Ce compte est associé à masubventionpro.com. Veuillez vous connecter sur masubventionpro.com.');
        }

        // ✅ Removed success toast - user doesn't need confirmation on every login
        onClose();

        // Check user's tier to determine redirect
        const { data: tierData } = await supabase
          .from('user_payment_status')
          .select('*')
          .eq('user_id', data.user.id)
          .single();

        const onboardingCompleted = data.user?.user_metadata?.onboarding_completed;

        // Determine redirect based on tier
        let redirectPath = '/onboarding';

        if (onboardingCompleted) {
          // Check if user has Discover tier (pay-as-you-go)
          const hasDiscoverTier = tierData?.credits_remaining > 0 && tierData?.subscription_status !== 'active';

          if (hasDiscoverTier) {
            // Discover users -> chatbot/calculator ONLY
            redirectPath = '/decouvrir';
            logger.debug('✅ Discover tier detected, redirecting to /decouvrir');
          } else {
            // All other tiers -> dashboard
            redirectPath = '/tableau-de-bord';
            logger.debug('✅ Full access tier, redirecting to dashboard');
          }
        }

        navigate(redirectPath);
      }
    } catch (error: any) {
      let errorMessage = t('auth.somethingWentWrong');

      if (error.message.includes('Invalid login credentials') || error.message.includes('Invalid email or password')) {
        errorMessage = t('auth.invalidCredentials');
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = t('auth.emailNotConfirmed');
      } else if (error.message.includes('Too many requests')) {
        errorMessage = t('auth.tooManyRequests');
      }

      toast({
        title: t('auth.loginFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);

    try {
      // Pre-validation: Check if user is trying to use Google for organization account
      const accountType = localStorage.getItem('pendingAccountType');
      if (accountType === 'organization') {
        toast({
          title: 'Google Login Not Available',
          description: 'Google login is only available for personal accounts. Please use email/password signup.',
          variant: 'destructive',
        });
        setGoogleLoading(false);
        return;
      }

      // 🔍 DEBUG: Log OAuth configuration being sent
      const oauthConfig = {
        provider: 'google' as const,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      };

      logger.debug('🔍 Google OAuth Debug Info:');
      logger.debug('  - Current Origin:', { origin: window.location.origin });
      logger.debug('  - Redirect URI:', { redirectTo: options.redirectTo });
      logger.debug('  - Full Config:', JSON.stringify(oauthConfig, null, 2));
      logger.debug('  - Expected Google Cloud Console URI:', { redirectTo: options.redirectTo });
      logger.debug('📋 Copy this for Google Cloud Console Authorized Redirect URIs:');
      logger.debug(`    ${oauthConfig.options.redirectTo}`);

      const { data, error } = await supabase.auth.signInWithOAuth(oauthConfig);

      logger.debug('🔍 Supabase OAuth Response:', {
        data: data, error: error, hasUrl: !!data?.url, urlPreview: data?.url ? data.url.substring(0, 100) + '...' : null
      });

      if (error) {
        logger.error('🚨 Google OAuth Error Details:', {
          message: error.message, status: error.status, details: error
        });
        
        // Handle specific OAuth errors with user-friendly messages
        if (error.message.includes('redirect_uri_mismatch')) {
          const debugMessage = `Redirect URI Mismatch!\n\nExpected: ${oauthConfig.options.redirectTo}\n\nAdd this exact URI to Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client IDs → Authorized redirect URIs`;
          
          logger.error('🚨 REDIRECT URI MISMATCH:', debugMessage);
          
          toast({
            title: 'Google OAuth Configuration Error',
            description: `Redirect URI mismatch. Check console for exact URI to add to Google Cloud Console.`,
            variant: 'destructive',
          });
          
          // Also show an alert with the exact URI
          setTimeout(() => {
            alert(`Google OAuth Setup Required:\n\nAdd this URI to Google Cloud Console:\n${oauthConfig.options.redirectTo}\n\nPath: APIs & Services → Credentials → OAuth 2.0 Client IDs → Authorized redirect URIs`);
          }, 1000);
          
        } else if (error.message.includes('access_denied') || error.message.includes('403')) {
          toast({
            title: 'Access Denied',
            description: 'Google login was cancelled or access was denied. Please try again or use email/password.',
            variant: 'destructive',
          });
        } else if (error.message.includes('Email not confirmed') || 
            error.message.includes('signup_disabled') ||
            error.message.includes('Invalid OAuth state') ||
            error.message.includes('user_not_found')) {
          toast({
            title: 'Account Not Found',
            description: 'This Google account isn\'t registered with subvention360 yet. Please sign up with email and password first.',
            variant: 'destructive',
          });
        } else if (error.message.includes('google_oauth_not_allowed_for_organizations')) {
          toast({
            title: 'Google Login Unavailable',
            description: 'Google login is only available for personal accounts. Please use email and password instead.',
            variant: 'destructive',
          });
        } else {
          throw error;
        }
      } else {
        // Success case - OAuth redirect will happen automatically
        logger.debug('✅ OAuth initiation successful, redirecting to Google...');
        // Close modal since user will be redirected
        onClose();
      }
    } catch (error: any) {
      logger.error('🚨 Google login error:', error);
      
      // Enhanced error handling for common OAuth issues
      if (error.message?.includes('redirect_uri_mismatch')) {
        const redirectUri = `${window.location.origin}/auth/callback`;
        logger.error('🚨 REDIRECT URI MISMATCH - Add this URI to Google Cloud Console:', redirectUri);
        
        toast({
          title: 'Configuration Error',
          description: 'Google OAuth redirect URI mismatch. Check browser console for setup instructions.',
          variant: 'destructive',
        });
        
        // Show setup instructions
        setTimeout(() => {
          alert(`Google Cloud Console Setup Required:\n\n1. Go https: //console.cloud.google.com/apis/credentials\n2. Find your OAuth 2.0 Client ID\n3. Add this URI to "Authorized redirect URIs":\n   ${redirectUri}\n\n4. Save changes and try again`);
        }, 1000);
        
      } else if (error.message?.includes('unauthorized_client')) {
        toast({
          title: 'Google Login Unavailable',
          description: 'Google login is temporarily unavailable. Please use email/password instead.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Login Failed',
          description: 'Something went wrong with Google login. Please try again or use email and password.',
          variant: 'destructive',
        });
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (signupData.password !== signupData.confirmPassword) {
      toast({
        title: t('auth.passwordsDoNotMatch'),
        description: t('auth.passwordsDoNotMatch'),
        variant: 'destructive',
      });
      setLoading(false);
      return;
    }

    try {
      const accountType = localStorage.getItem('pendingAccountType') || 'user';

      // Check beta signup eligibility if on beta subdomain
      const betaSignupResult = await prepareBetaSignup(signupData.email);

      if (!betaSignupResult.allowed) {
        // Beta limit reached - user added to waitlist
        toast({
          title: '🧪 Beta is Full!',
          description: betaSignupResult.message || 'You\'ve been added to our waitlist. We\'ll notify you when a spot opens up!',
          variant: 'default',
        });
        setLoading(false);
        return;
      }

      // Prepare signup metadata
      const userMetadata: Record<string, any> = {
        source: 'subvention360', // Platform identifier for cross-platform auth
        account_type: accountType,
      };

      // If beta environment, add beta metadata
      if (isBetaEnvironment()) {
        const betaMetadata = getBetaMetadata();
        Object.assign(userMetadata, betaMetadata);
        logger.info('🧪 Beta signup - adding beta metadata:', betaMetadata);
      }

      const { data, error } = await supabase.auth.signUp({
        email: signupData.email,
        password: signupData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: userMetadata,
        }
      });

      if (error) throw error;

      // Check if user already exists (Supabase security feature)
      // When email exists, it returns user but with empty identities array
      if (data.user && (!data.user.identities || data.user.identities.length === 0)) {
        logger.warn('⚠️ Email already registered:', signupData.email);
        throw new Error('This email is already registered. Please sign in instead.');
      }

      if (data.user) {
        // Track beta signup if applicable
        if (isBetaEnvironment()) {
          await trackBetaSignup(data.user.id, signupData.email);
          logger.info('✅ Beta signup tracked for user:', data.user.id);
        }

        logger.info('📧 Account created, verification code sent to:', signupData.email);

        // signUp() automatically sends a confirmation email with OTP code
        // No need to call signInWithOtp() - that's for magic link login, not signup verification

        // Show email verification screen
        setPendingVerificationEmail(signupData.email);
        setShowEmailVerification(true);

        // Don't navigate yet - wait for email verification
      }
    } catch (error: any) {
      logger.error('Signup error details:', error);
      let errorMessage = t('auth.signupFailed');

      if (error.message.includes('already registered') ||
          error.message.includes('User already registered') ||
          error.message.includes('This email is already registered')) {
        errorMessage = error.message.includes('This email is already registered')
          ? 'This email is already registered. Redirecting to login...'
          : t('auth.alreadyRegistered');

        toast({
          title: 'Account Already Exists',
          description: errorMessage,
          variant: 'default',
        });

        // Auto-switch to login mode
        setTimeout(() => {
          onModeChange('login');
          setLoginData({ email: signupData.email, password: '' });
        }, 2000);

        setLoading(false);
        return; // Don't show error toast again
      } else if (error.message.includes('Password should be at least')) {
        // Show the actual Supabase password requirement error
        errorMessage = error.message;
      } else if (error.message.includes('password') || error.message.includes('Password')) {
        // For other password-related errors, show the actual message
        errorMessage = error.message || t('auth.passwordsDoNotMatch');
      } else if (error.message.includes('invalid email')) {
        errorMessage = t('auth.invalidEmail');
      } else if (error.message) {
        // Show the actual error message from Supabase for better debugging
        errorMessage = error.message;
      }

      toast({
        title: t('auth.signupFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerified = () => {
    logger.info('✅ Email verified, navigating to onboarding');

    toast({
      title: t('auth.accountCreated'),
      description: isBetaEnvironment()
        ? '🧪 Welcome to the beta! You have 7 days of full access.'
        : 'Your account has been verified successfully!',
    });

    onClose();
    navigate('/onboarding');
  };

  const handleCancelVerification = () => {
    setShowEmailVerification(false);
    setPendingVerificationEmail('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-2xl border-border">
        {showEmailVerification ? (
          // Email Verification Screen
          <>
            <DialogHeader>
              <DialogTitle className="text-foreground">
                Verify Your Email
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Enter the 6-digit code we sent to your email
              </DialogDescription>
            </DialogHeader>

            <EmailVerificationScreen
              email={pendingVerificationEmail}
              onVerified={handleEmailVerified}
              onCancel={handleCancelVerification}
            />
          </>
        ) : (
          // Normal Login/Signup Flow
          <>
            <DialogHeader>
              <DialogTitle className="text-foreground">
                {mode === 'login' ? t('auth.loginTitle') : t('auth.signupTitle')}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {mode === 'login'
                  ? t('auth.loginDescription')
                  : t('auth.signupDescription')
                }
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">{t('auth.email')}</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder={t('auth.enterEmail')}
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password">{t('auth.password')}</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t('auth.enterPassword')}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full" disabled={loading}>
                {loading ? t('auth.loggingIn') : t('auth.login')}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signup-email">{t('auth.email')}</Label>
                <Input
                  id="signup-email"
                  type="email"
                  placeholder={t('auth.enterEmail')}
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="signup-password">{t('auth.password')}</Label>
                <div className="relative">
                  <Input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t('auth.enterPassword')}
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      required
                      className="pr-10"
                      minLength={6}
                      autoComplete="new-password"
                    />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  value={signupData.confirmPassword}
                  onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full" disabled={loading}>
                {loading ? t('auth.signingUp') : t('auth.signUp')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          )}

          {/* Google Login - Temporarily Hidden
          {mode === 'login' && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  className="w-full border-border hover:border-foreground rounded-full"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  {googleLoading ? 'Connecting...' : 'Continue with Google'}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  ✓ Available for personal accounts only
                </p>
              </div>
            </>
          )}
          */}

          {/* Mode Switch */}
          <div className="text-center pt-4 border-t">
            {mode === 'login' ? (
              <p className="text-sm text-muted-foreground">
                {t('auth.noAccount')}{' '}
                <button
                  type="button"
                  onClick={() => onModeChange('signup')}
                  className="text-primary hover:underline font-medium"
                >
                  {t('auth.signUpHere')}
                </button>
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('auth.alreadyHaveAccount')}{' '}
                <button
                  type="button"
                  onClick={() => onModeChange('login')}
                  className="text-primary hover:underline font-medium"
                >
                  {t('auth.signInHere')}
                </button>
              </p>
            )}
          </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};